'use client'

import { memo, useCallback, useState } from 'react'

import { BaseSelectProps, ChevronIcon, MSItemComponent } from './SelectContent'
import { Button } from '../button/Button'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandList } from '../command/Command'
import { cn } from '../lib/cn'
import { Popover, PopoverContent, PopoverTrigger } from '../popover/Popover'

const MATCH = 1
const NO_MATCH = 0

const filterBySearchInLabel = (_optionId: string, search: string, keywords?: string[]) => {
  const [label = ''] = keywords ?? []
  const labelMatchesSearch = label.toLowerCase().includes(search.toLowerCase())

  return labelMatchesSearch ? MATCH : NO_MATCH
}

interface SingleSelectProps extends BaseSelectProps {
  value: string | null
  onChange: (value: string | null) => void
  selectClassName?: string
  isToggleEnabled?: boolean
  isAutoOpen?: boolean
  onInteractOutside?: () => void
}

export const SingleSelect = memo(
  ({
    value,
    onChange,
    options,
    placeholder = 'Select…',
    selectClassName,
    disabled = false,
    modal = false,
    isToggleEnabled = true,
    isAutoOpen = false,
    onInteractOutside,
  }: SingleSelectProps) => {
    const [open, setOpen] = useState(isAutoOpen)

    const handleOpenChange = useCallback(
      (newOpen: boolean) => {
        setOpen(newOpen)
        if (!newOpen) {
          onInteractOutside?.()
        }
      },
      [onInteractOutside],
    )

    const toggleValue = useCallback(
      (val?: string) => {
        const isToggleToNull = isToggleEnabled && value === val

        const newValue = isToggleToNull ? null : val || '-'

        onChange(newValue)
        handleOpenChange(false)
      },
      [onChange, isToggleEnabled, value, handleOpenChange],
    )

    const renderTrigger = () => {
      const currentOption = options.find((o) => o.value === value)
      const Icon = currentOption?.icon

      return (
        <div className='flex justify-between items-center w-full'>
          <span
            className={cn(
              'text-sm font-normal overflow-hidden',
              currentOption ? 'text-foreground' : 'text-muted-foreground',
            )}
          >
            {Icon && <Icon className='h-4 w-4 mr-2' />}
            <div role='marquee' className={cn('flex items-center', open && 'hover:animate-marquee relative')}>
              {currentOption?.label ?? placeholder}
            </div>
          </span>
          <ChevronIcon open={open} />
        </div>
      )
    }

    return (
      <Popover open={open} onOpenChange={handleOpenChange} modal={modal}>
        <PopoverTrigger asChild>
          <Button
            disabled={disabled}
            variant='outline'
            className={cn(
              'flex min-h-[38px] h-auto px-3 py-1.5 rounded-lg border-input items-center justify-between bg-card hover:bg-inherit [&_svg]:pointer-events-auto',
              selectClassName,
            )}
          >
            {renderTrigger()}
          </Button>
        </PopoverTrigger>
        <PopoverContent className='p-0 w-max min-w-(--radix-popover-trigger-width) max-w-80' align='start'>
          <Command filter={filterBySearchInLabel}>
            <CommandInput placeholder='Search...' wrapperClasses='border-b-input' />
            <CommandList>
              <CommandEmpty>No results found.</CommandEmpty>
              <CommandGroup>
                {options.map((option) => (
                  <MSItemComponent
                    value={value}
                    onlyOneSelect
                    toggleValue={toggleValue}
                    key={option.value}
                    option={option}
                  />
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    )
  },
)

SingleSelect.displayName = 'SingleSelect'
